import { __decorate } from "tslib";
import { BaseModule, Module } from "@fastoche/core";
import { TranslationStore } from "./translation/TranslationStore";
import { TranslationService } from "./translation/TranslationService";
import { DefaultTranslationCache, TranslationCache } from "./translation/TranslationCache";
import { TranslationProvider } from "./translation/provider/TranslationProvider";
import { LocalizationOptionsProvider } from "./options/LocalizationOptionsProvider";
import { LocalizationOptionsStore } from "./options/LocalizationOptions";
import { LocalizationOptionsService } from "./options/LocalizationOptionsService";
import { UserLocalizationConfigStore } from "./user/UserLocalizationConfigStore";
import { UserLocalizationConfigStorage } from "./user/UserLocalizationConfigStorage";
import { LocalizationContext } from "./context";
import { UserLocalizationConfigService } from "./user/UserLocalizationConfigService";
import { LocaleSource } from "./locale/sources/LocaleSource";
import { UserLocaleResolver } from "./user/resolver/UserLocaleResolver";
import { OrderedSourceLocaleResolver } from "./user/resolver/OrderedSourceLocaleResolver";
import { LibraryLocaleAdapter } from "./libs/LibraryLocaleAdapter";
import { bindSingletonImplementation } from "../di";
import { TranslationEditorContext } from "./translation/editor/TranslationEditorContext";
import { NavigatorLocaleSource } from "./locale/sources/NavigatorLocaleSource";
/**
 * i18n management module
 */
let CoreI18nModule = class CoreI18nModule extends BaseModule {
    registerServices(container, config) {
        // Apply default settings
        config.locale.detection ??= {
            sources: [NavigatorLocaleSource],
        };
        // Localization options
        if (config.options) {
            bindSingletonImplementation(container, LocalizationOptionsProvider, config.options.provider);
        }
        container.bind(LocalizationOptionsStore).toConstantValue(new LocalizationOptionsStore({
            defaultCurrency: config.currency.default,
            defaultLocale: config.locale.default,
            wellKnownLocales: [config.locale.default],
        }));
        container.bind(LocalizationOptionsService).toSelf().inSingletonScope();
        // Translations
        if (config.translations) {
            bindSingletonImplementation(container, TranslationProvider, config.translations.provider);
        }
        container.bind(TranslationStore).toConstantValue(new TranslationStore({
            locale: config.locale.default,
            translations: new Map(),
        }));
        container.bind(TranslationCache).to(DefaultTranslationCache).inSingletonScope();
        container.bind(TranslationService).toSelf().inSingletonScope();
        container.bind(TranslationEditorContext).toSelf().inSingletonScope();
        // Locale sources
        for (const source of config.locale.detection.sources) {
            container.bind(LocaleSource).to(source).inSingletonScope();
        }
        // User configuration
        container.bind(UserLocalizationConfigStore).toSelf().inSingletonScope();
        container.bind(UserLocalizationConfigStorage).toSelf().inSingletonScope();
        container.bind(UserLocalizationConfigService).toSelf().inSingletonScope();
        container.bind(UserLocaleResolver).to(OrderedSourceLocaleResolver.withConfig({ order: config.locale.detection.sources })).inSingletonScope();
        // Localization context
        container.bind(LocalizationContext).toSelf().inSingletonScope();
    }
    async configureServices(container, config) {
        const localizationOptionsService = container.get(LocalizationOptionsService);
        await localizationOptionsService.initializeOptions();
        const userLocalizationConfigService = container.get(UserLocalizationConfigService);
        await userLocalizationConfigService.initializeUserConfiguration();
        const userLocalizationConfigStore = container.get(UserLocalizationConfigStore);
        userLocalizationConfigStore.registerListeners();
        const localizationContext = container.get(LocalizationContext);
        localizationContext.registerListeners();
        const translationService = container.get(TranslationService);
        translationService.registerListeners();
        if (container.isBound(LibraryLocaleAdapter)) {
            const libraryAdapters = container.getAll(LibraryLocaleAdapter);
            libraryAdapters.forEach(x => x.registerListeners());
        }
    }
    applicationReady(hook) {
        const { rootContainer } = hook.applicationContext;
        const translationEditorContext = rootContainer.get(TranslationEditorContext);
        translationEditorContext.detectModeFromUrl();
    }
};
CoreI18nModule = __decorate([
    Module()
], CoreI18nModule);
export { CoreI18nModule };
//# sourceMappingURL=module.js.map